import { Upload, Sparkles, Download, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { m } from "framer-motion";
import { useInView } from "react-intersection-observer";

export const HowItWorks = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const steps = [
    {
      number: "01",
      icon: Upload,
      title: "Connect Your Figma",
      description: "Paste your Figma URL or use our plugin. Works with any Figma file - public or private.",
      features: ["Figma plugin available", "Secure OAuth connection", "Works with any file"],
    },
    {
      number: "02",
      icon: Sparkles,
      title: "AI Does the Magic",
      description: "Watch as AI analyzes your designs and extracts every detail automatically in seconds.",
      features: ["AI-powered extraction", "Design system detection", "Component analysis"],
    },
    {
      number: "03",
      icon: Download,
      title: "Export & Ship",
      description: "Choose your format and share with your team. Updates automatically when designs change.",
      features: ["Multiple export formats", "Real-time updates", "Team collaboration"],
    },
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-muted/20 to-background" id="how-it-works" ref={ref}>
      <div className="container-custom">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Three Steps to <span className="gradient-text">Perfect Documentation</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From Figma file to developer-ready documentation in under 60 seconds
          </p>
        </m.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <m.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="relative"
              >
                {/* Connecting line (desktop only) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-20 left-full w-8 h-0.5 bg-gradient-to-r from-primary to-transparent z-0">
                    <ArrowRight className="absolute -right-4 -top-2 h-5 w-5 text-primary" />
                  </div>
                )}

                <div className="glass-card p-8 hover-glow hover-lift relative z-10">
                  {/* Step number */}
                  <div className="text-6xl font-bold text-primary/20 mb-4">{step.number}</div>

                  {/* Icon */}
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#667eea]/20 to-[#764ba2]/20 flex items-center justify-center mb-6">
                    <Icon className="h-8 w-8 text-primary" />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                  <p className="text-muted-foreground mb-6">{step.description}</p>

                  {/* Features */}
                  <ul className="space-y-2">
                    {step.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </m.div>
            );
          })}
        </div>

        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center"
        >
          <Button
            size="lg"
            variant="gradient"
            className="text-lg px-8"
          >
            Start Your Free Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </m.div>
      </div>
    </section>
  );
};
